import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import comms from '../comms';
import LocationCreate from './LocationCreate';

function distanceMiles(a, b) {
  const R = 3958.8;
  const rad = (x) => (x * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function LocationPicker({ open, onClose, onSelect, driverId, online, onToast }) {
  const [locations, setLocations] = useState([]);
  const [status, setStatus] = useState('idle');
  const [query, setQuery] = useState('');
  const [here, setHere] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setCreating(false);
    setStatus('loading');
    comms
      .getVehicleLocations()
      .then((res) => {
        // API may return res.locations, res.Locations, or a direct array
        const list =
          Array.isArray(res?.locations) ? res.locations :
          Array.isArray(res?.Locations) ? res.Locations :
          Array.isArray(res)            ? res           : [];
        setLocations(list);
        setStatus('loaded');
      })
      .catch(() => setStatus('error'));

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setHere({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {},
        { maximumAge: 60000, timeout: 5000 }
      );
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? locations.filter(
          (l) =>
            String(l.Name || l.name || '').toLowerCase().includes(q) ||
            String(l.ZoneName || l.zoneName || '').toLowerCase().includes(q) ||
            String(l.Description || l.description || '').toLowerCase().includes(q)
        )
      : locations;
    if (!here) return list;
    return [...list].sort((a, b) => {
      const da = distanceMiles(here, { lat: a.Latitude ?? a.latitude, lng: a.Longitude ?? a.longitude });
      const db = distanceMiles(here, { lat: b.Latitude ?? b.latitude, lng: b.Longitude ?? b.longitude });
      return da - db;
    });
  }, [locations, query, here]);

  return (
    <>
      <Dialog
        open={open && !creating}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ style: { borderRadius: 20, margin: 16, maxHeight: 'calc(100% - 32px)' } }}
      >
        <DialogTitle style={{ padding: '20px 20px 4px', fontSize: 17, fontWeight: 800, color: '#0F172A' }}>
          Choose a location
        </DialogTitle>

        <DialogContent style={{ padding: '12px 20px 4px' }}>
          {/* Search */}
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search locations…"
            autoFocus
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '10px 14px', marginBottom: 12,
              border: '1.5px solid #E2E8F0', borderRadius: 12,
              fontSize: 14, color: '#0F172A', outline: 'none',
              background: '#F8FAFC',
            }}
            onFocus={(e) => { e.target.style.borderColor = '#3B5BDB'; }}
            onBlur={(e)  => { e.target.style.borderColor = '#E2E8F0'; }}
          />

          {/* Loading */}
          {status === 'loading' && (
            <div style={{ padding: '24px 0', textAlign: 'center', color: '#94A3B8', fontSize: 14 }}>
              Loading locations…
            </div>
          )}

          {/* Error */}
          {status === 'error' && (
            <div style={{ padding: '24px 0', textAlign: 'center', color: '#EF4444', fontSize: 14 }}>
              Could not load locations — close and try again.
            </div>
          )}

          {/* Empty */}
          {status === 'loaded' && !filtered.length && (
            <div style={{ padding: '24px 0', textAlign: 'center', color: '#94A3B8', fontSize: 14 }}>
              {query ? 'No locations match your search.' : 'No locations yet — add one below.'}
            </div>
          )}

          {/* List */}
          {status === 'loaded' && filtered.map((loc) => {
            const name = loc.Name || loc.name || '';
            const sub  = loc.ZoneName || loc.zoneName || loc.Description || loc.description || '';
            const lat  = loc.Latitude  ?? loc.latitude;
            const lng  = loc.Longitude ?? loc.longitude;
            const miles = here && lat != null ? distanceMiles(here, { lat, lng }) : null;
            return (
              <button
                key={loc.Id || loc.id}
                type="button"
                onClick={() => onSelect(loc)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', textAlign: 'left',
                  padding: '12px 14px', marginBottom: 6,
                  background: '#F8FAFC', border: '1.5px solid #E2E8F0',
                  borderRadius: 12, cursor: 'pointer',
                }}
                onMouseOver={(e) => { e.currentTarget.style.background = '#EFF6FF'; e.currentTarget.style.borderColor = '#3B5BDB'; }}
                onMouseOut={(e)  => { e.currentTarget.style.background = '#F8FAFC'; e.currentTarget.style.borderColor = '#E2E8F0'; }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {name}
                  </div>
                  {sub && (
                    <div style={{ fontSize: 12, color: '#64748B', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {sub}
                    </div>
                  )}
                </div>
                {miles != null && (
                  <span style={{ marginLeft: 10, flexShrink: 0, fontSize: 12, color: '#94A3B8' }}>
                    {miles < 10 ? miles.toFixed(1) : Math.round(miles)} mi
                  </span>
                )}
              </button>
            );
          })}
        </DialogContent>

        <DialogActions style={{ padding: '12px 20px 20px' }}>
          <button
            type="button"
            onClick={() => setCreating(true)}
            disabled={!online}
            style={{
              width: '100%', padding: '13px', borderRadius: 14,
              border: '2px solid #3B5BDB', background: '#EFF6FF',
              color: '#3B5BDB', fontSize: 14, fontWeight: 700,
              cursor: online ? 'pointer' : 'not-allowed',
              opacity: online ? 1 : 0.5,
            }}
          >
            + Add new location
          </button>
        </DialogActions>
      </Dialog>

      <LocationCreate
        open={creating}
        onClose={() => setCreating(false)}
        driverId={driverId}
        online={online}
        onToast={onToast}
        onCreated={(loc) => {
          setCreating(false);
          onSelect(loc);
        }}
      />
    </>
  );
}

export default LocationPicker;
